// Curated site-intelligence content for the KEIP dossier (`/cluster/pike-keip`).
//
// This is bespoke, editorially-curated content for ONE site — the Kentucky
// Enterprise Industrial Park — framed as a GRID-CONNECTED data-center site (not
// behind-the-meter). It layers real, citable facts (the executed MD Squared
// Power MOU, the ZoomProspector datasheet, the KY Cabinet for Economic
// Development build-ready package, and the local power-infrastructure data) on
// top of the live `/api/clusters/:slug` grid + tract analysis.
//
// Keyed by slug so any other cluster renders the analysis sections cleanly with
// no dossier blocks.

export interface DossierFact {
  label: string;
  value: string;
  source?: string;
}

export interface DossierPanel {
  key: string;
  title: string;
  facts: DossierFact[];
  note?: string;
}

export interface DossierDesignation {
  label: string;
  detail?: string;
}

export interface DossierDoc {
  title: string;
  category: 'Site & Survey' | 'Engineering' | 'Environmental' | 'Buildings' | 'Financial';
  // Download link — self-hosted copy under public/keip/docs/ (state-published
  // build-ready package, pulled from the KCED / ZoomProspector listing), or an
  // external state-hosted URL. Docs without a url render as available on request.
  url?: string;
}

export interface SiteDossier {
  eyebrow: string;
  title: string;
  subtitle: string;
  dataAsOf: string;
  hero: {
    webp: string;
    jpg: string;
    alt: string;
    headline: string;
    sub: string;
  };
  heroStats: DossierFact[];
  deal: {
    status: string;
    counterparty: string;
    date: string;
    // MOU tracts with Exhibit A's stated acreage (omitted where the MOU leaves
    // it unstated, e.g. C-1) — the deal block is where MOU acreage lives.
    tracts: { id: string; acres?: string }[];
    terms: string;
    // Lead line — the City's posture, stated up front (the "why this is live").
    lede: string;
  };
  factSheet: DossierPanel[];
  designations: DossierDesignation[];
  dataRoom: DossierDoc[];
  analysisNote: string;
  // Curated occupant overrides keyed by display tract label (MOU style, e.g.
  // 'A-1') — for parcels the county record still lists under the park owner but
  // which are, on the ground, held or occupied by a named party (so the
  // map/list don't imply availability).
  occupants?: Record<string, string>;
  // Curated tract overrides keyed by display tract label — for tracts whose
  // state-GIS coding hides what they are on the ground (e.g. the MOU's C-1 is
  // TRACT_ID '99' / status 'other' in the state layer). `offPad` marks a tract
  // whose flat ground does NOT connect to the contiguous pad — the map shows no
  // green there and the popup omits the buildable line.
  tractOverrides?: Record<string, { label?: string; status?: string; offPad?: boolean }>;
  // Static flat-ground overlay for the map (ground under 5% slope, with the
  // MOU-tract portion emphasized) — precomputed Atwater terrain analysis.
  flatOverlayUrl?: string;
}

const KEIP: SiteDossier = {
  eyebrow: 'Site Intelligence Dossier',
  title: 'Kentucky Enterprise Industrial Park',
  subtitle: 'Grid-Connected Data-Center Candidate · Pikeville, KY · Pike County · Site 195-013',
  dataAsOf: 'July 2026',

  hero: {
    webp: '/keip/aerial.webp',
    jpg: '/keip/aerial.jpg',
    alt: 'Aerial view of the graded, build-ready KEIP pad on a reclaimed Appalachian bench with a new access road',
    headline: 'One contiguous flat pad on a 180-acre build-ready park',
    sub: 'A grid-connected data-center candidate on a reclaimed Appalachian bench — 765 kV transmission two miles out, in an Energy Community, under exclusive MOU with MD Squared Power.',
  },

  heroStats: [
    { label: 'Available', value: '~180 ac available · ~54-ac contiguous flat' },
    { label: 'Ownership', value: 'City of Pikeville · all five MOU tracts' },
    { label: 'Utility', value: 'AEP Kentucky Power · PJM' },
    { label: 'EHV transmission', value: '765 kV @ 2.2 mi' },
    { label: 'Nearest 138 kV', value: 'Cedar Creek substation @ 3.8 mi' },
    { label: 'Federal', value: 'Opportunity Zone + Energy Community' },
  ],

  deal: {
    status: 'Executed Memorandum of Understanding',
    counterparty: 'MD Squared Power LLC',
    date: 'April 20, 2026',
    tracts: [
      { id: 'A-3', acres: '15± ac' },
      { id: 'A-4', acres: '37.9± ac' },
      { id: 'A-5', acres: '10.7± ac' },
      { id: 'A-6', acres: '21.3± ac' },
      { id: 'C-1' },
    ],
    terms:
      '120-day exclusivity period · conveyance by special warranty deed, free and clear of liens and encumbrances, or lease under a negotiated Development Agreement · closing within 60 days after the due-diligence period ends · automatic reversion to the City if development criteria aren’t met · the City represents no competing agreement exists on the Property · only the exclusivity provision is binding until a Development Agreement is executed.',
    lede:
      'The City of Pikeville wants this site developed. Its executed MOU sets out to convey or lease these five tracts — for the tax base and jobs the project would bring — and binds the City to deal with no other party through August 18, 2026.',
  },

  factSheet: [
    {
      key: 'power',
      title: 'Power & Interconnection',
      facts: [
        { label: 'Utility', value: 'AEP Kentucky Power' },
        { label: 'Grid', value: 'PJM Interconnection' },
        { label: 'EHV line', value: '765 kV @ 2.2 mi · Baker–Broadford' },
        { label: 'Nearest 138 kV', value: 'Cedar Creek sub @ 3.8 mi' },
        { label: 'Local service', value: '46 kV Kentucky Power @ 1.1 mi' },
      ],
      note: 'Grid-served load — interconnection would proceed through the Kentucky Power (AEP) / PJM queue.',
    },
    {
      key: 'terrain',
      title: 'Terrain · Building Pad',
      facts: [
        { label: 'Flat pad, MOU tracts', value: '~54 ac · one contiguous piece' },
        { label: 'Beyond the pad', value: 'Flat surface continues across the park (see map)' },
        { label: 'Certified pad', value: 'Build-Ready · 100,000 sq ft (Lot A-3)' },
      ],
      note: 'Tract averages understate the site — the flat portions of A-3, A-6, and A-4 merge into a single pad on a reclaimed, state-released surface-mine bench (permit 8980457). Atwater terrain model on USGS 10-m elevation; flat = under 5% slope; official tract acreages follow MOU Exhibit A.',
    },
    {
      key: 'environment',
      title: 'Environmental Screen',
      facts: [
        { label: 'Air quality', value: 'EPA attainment · all pollutants' },
        { label: 'Floodplain', value: 'None mapped on pad · Zone A ~0.3 mi' },
        { label: 'Undermining', value: 'None beneath pad · voids ~0.2 mi' },
        { label: 'Seismic', value: '≈0.11g PGA (USGS NSHM)' },
        { label: 'Listed species', value: '6 county-wide · incl. 3 bat spp.' },
      ],
      note: 'Screens from FEMA NFHL, Kentucky Geological Survey mined-out-area mapping, USGS seismic hazard, and EPA attainment data. No mapped 100-year floodplain or underground mine void touches the five MOU tracts; distances are from the contiguous pad. Species are county-level listings; seasonal tree-clearing windows are the standard bat mitigation. Phase I ESA and WOTUS assessment are in the state build-ready package.',
    },
    {
      key: 'connectivity',
      title: 'Connectivity · Fiber',
      facts: [
        { label: 'Providers', value: 'AT&T · Windstream · Suddenlink · Inter Mountain Cable' },
        { label: 'Verified on-site', value: '1 Gbps enterprise fiber' },
      ],
      note: 'On-site enterprise fiber service verified in the FCC Broadband Data Collection (July 2026 filing). Long-haul and dark-fiber routing to be confirmed in diligence.',
    },
    {
      key: 'water',
      title: 'Water · Cooling',
      facts: [
        { label: 'Provider', value: 'Pikeville Water' },
        { label: 'Storage', value: 'New 1.0M-gal tank + 300K-gal tank' },
        { label: 'Treatment', value: '4.5M GPD plant capacity' },
        { label: 'Climate', value: 'Design high 86°F · 1,234 CDD/yr' },
      ],
      note: 'Ample supply for closed-loop or evaporative cooling; 8" main available to Lot A-3. Mild Appalachian design temperatures (NOAA normals) favor economizer hours relative to Sun Belt sites.',
    },
    {
      key: 'sewer',
      title: 'Sewer',
      facts: [
        { label: 'Provider', value: 'Pikeville Sewer' },
        { label: 'Current plant', value: '2.0M GPD' },
        { label: 'Expandable to', value: '4.0M → 12.0M GPD' },
        { label: 'At Lot A-3', value: 'Available now' },
      ],
      note: 'A $22M membrane treatment plant doubles capacity to 4.0M GPD, with room to triple to ~12M GPD by adding membranes within the same footprint.',
    },
    {
      key: 'gas',
      title: 'Natural Gas',
      facts: [
        { label: 'On-site lines', value: '2 transmission lines' },
        { label: 'Operators', value: 'Diversified · L&B Oil & Gas' },
        { label: 'Wells', value: 'Existing wells in park' },
      ],
      note: 'On-site gas is a backup-generation and dual-fuel resiliency option for a grid-connected load — two lines run through the park, with existing wells available.',
    },
    {
      key: 'transportation',
      title: 'Transportation & Access',
      facts: [
        { label: 'Highway', value: 'Adjacent to 4-lane US-23' },
        { label: 'Interstates', value: 'I-64 ~95 mi · I-26 ~99 mi' },
        { label: 'Private jet airport', value: 'Pike County Rgnl · ~9 mi (20 min)' },
        { label: 'Commercial airport', value: 'Huntington Tri-State · 98 mi drive' },
        { label: 'Rail', value: 'CSX main line 1.3 mi · siding ~8 mi' },
        { label: 'Riverport', value: 'Port of Wurtland · 117 mi' },
      ],
      note: 'Four-lane US-23 runs north to I-64 (~95 mi) and south to I-26 at Kingsport (~99 mi); it also links to KY 80 (~25 mi, west to I-75), US-119 (north to I-77), and the Bert T. Combs Mountain Parkway via KY 114 (~30 mi). Commercial airports (drive distances): HTS 98 mi, Tri-Cities 107 mi, Yeager 120 mi, Lexington 154 mi.',
    },
    {
      key: 'workforce',
      title: 'Workforce',
      facts: [
        { label: 'Available workers', value: '7,500' },
        { label: 'Non-union', value: '95%' },
        { label: 'Avg hourly earnings', value: '$17.45' },
        { label: 'Avg commute', value: '25.3 min' },
      ],
      note: 'One East Kentucky labor-shed figures for the Pikeville region — a coal-transition workforce with heavy-industrial trades experience.',
    },
  ],

  designations: [
    { label: 'Energy Community', detail: 'IRA clean-energy bonus credit — Appalachian coal-transition zone' },
    { label: 'Build-Ready Certified', detail: 'KY Cabinet for Economic Development — pad certified 07/15/2025 (Palmer Engineering / UES geotech)' },
    { label: 'Shovel-Ready Approved' },
    { label: 'Federal Opportunity Zone' },
    { label: 'New Markets Tax Credit eligible' },
    { label: 'KY Product Development Initiative', detail: 'KPDI project approved 03/28/2024' },
    { label: 'Enhanced Incentive County', detail: 'KCED / KBI' },
    { label: 'Regional Site 05', detail: 'State regional-site designation' },
    { label: 'Zone I — Manufacturing', detail: 'Heavy & light industrial zoning' },
  ],

  dataRoom: [
    { title: 'KEIP Site Plat — Lot A-3', category: 'Site & Survey', url: '/keip/docs/site-plat-lot-a3.pdf' },
    { title: 'Master Site Development Plan', category: 'Site & Survey', url: '/keip/docs/master-site-development-plan.pdf' },
    { title: 'Topo Map — TM195-013', category: 'Site & Survey', url: '/keip/docs/topo-map.pdf' },
    { title: 'Site Map — SM195-013', category: 'Site & Survey', url: '/keip/docs/site-map.pdf' },
    { title: 'Aerial Map — AM195-013', category: 'Site & Survey', url: '/keip/docs/aerial-map.pdf' },
    { title: 'Zoning Map', category: 'Site & Survey' },
    { title: 'KEIP Booklet (2024)', category: 'Site & Survey', url: '/keip/docs/keip-booklet-2024.pdf' },
    { title: 'Build-Ready Certification Letter', category: 'Engineering', url: '/keip/docs/build-ready-certification-letter.pdf' },
    { title: 'Utilities Extension Estimate', category: 'Engineering', url: '/keip/docs/utilities-extension-estimate.pdf' },
    { title: 'Utilities Extension Plans', category: 'Engineering', url: '/keip/docs/utilities-extension-plans.pdf' },
    { title: 'Road Extension Plan', category: 'Engineering', url: '/keip/docs/road-extension-plan.pdf' },
    { title: 'Build-Ready Site Specifications', category: 'Engineering' },
    { title: 'Geotechnical Report (Oct 2024)', category: 'Engineering', url: '/keip/docs/geotech-2024.pdf' },
    { title: 'Phase I ESA — Lot A-3 (Sep 2024)', category: 'Environmental', url: '/keip/docs/phase-1-esa-lot-a3.pdf' },
    { title: 'WOTUS Assessment (Aug 2024)', category: 'Environmental', url: '/keip/docs/wotus-assessment-2024.pdf' },
    { title: 'Stormwater Pollution Prevention Plan (SWPPP)', category: 'Environmental', url: '/keip/docs/swppp.pdf' },
    { title: 'KEIP Funding Plan', category: 'Financial' },
    { title: 'Build-Ready Tract Sale Price', category: 'Financial' },
    { title: 'Complete Build-Ready Package (ZIP · 118 MB)', category: 'Site & Survey', url: 'https://newkentuckyhome.ky.gov/edis/PDF/Site/Pike/KEIP_Build-Ready_Site_Support_Documentation.zip' },
  ],

  analysisNote:
    'Tract-level acreage, buildability, and slope figures on the map and in the tract list are Atwater modeled estimates from terrain analysis — developable = ground under 10% slope, flat = under 5%. Tract acreages are measured from state-GIS polygons and can differ from the stated acreages in MOU Exhibit A (A-3 maps at ~22 ac against 15 ± stated); official tract acreages follow Exhibit A. On availability: Exhibit A — the Commonwealth’s site map for 195-013, dated 9/30/2025 — lists 190.0 acres available. On that same date the City conveyed 10.14 ac of Tract B-2 to Wrightway Land LLC (Pike County PVA parcel 052-00-00-021.03, deed 1155/502), and the state GIS still codes B-2 vacant, so availability reads ~180 ac here. Tracts A-1 and A-2 (Daveclo Properties U S Inc, deed 1107/282, 2021) are already coded occupied and sit outside both figures. All five MOU tracts — A-3, A-4, A-5, A-6 and C-1 — remain City-held and are unaffected by either conveyance.',

  occupants: {
    'A-1': 'Appalachian Tank (Platinum Tank Group)',
    // A-1 and A-2 are one PVA parcel (052-00-00-021.02, deed 1107/282,
    // "Surveyed Deed Lots A-1 A-2") owned by Daveclo since 2021. A-1 carries the
    // operating tenant the state map names; A-2 names the owner, because nothing
    // in the record says Appalachian Tank occupies it too.
    'A-2': 'Daveclo Properties US Inc',
    'B-1': 'Wright Concrete',
    'B-2': 'Wrightway Land LLC',
  },

  tractOverrides: {
    // The state GIS has no 'C-1' label — the MOU's C-1 is the central tract
    // coded TRACT_ID '99', status 'other'. Show it as the deal tract it is.
    // Its flat pockets don't connect to the pad, so it renders un-greened.
    '99': { label: 'C-1', status: 'vacant', offPad: true },
    'B-8': { offPad: true },
    // The state GIS still has B-2 vacant, but the City sold 10.14 of its
    // 10.77 ac to Wrightway Land LLC on 2025-09-30 (Pike PVA parcel
    // 052-00-00-021.03, deed 1155/502) — and 4.15 of the 4.26 ac we shaded
    // as flat ground on B-2 is inside that sale. Corrected here rather than
    // in site_cluster_parcels.status, which seed-keip-cluster.js overwrites
    // from the state GIS on every re-seed.
    'B-2': { status: 'occupied' },
  },

  flatOverlayUrl: '/keip/flat-ground.geojson',
};

const DOSSIERS: Record<string, SiteDossier> = {
  'pike-keip': KEIP,
};

export function getSiteDossier(slug: string): SiteDossier | null {
  return DOSSIERS[slug] ?? null;
}
